// src/components/authorizations/AuthorizationDrawer.tsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  List,
  PasswordInput,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { DeleteSection } from "@/components/DeleteSection";
import { notifications } from "@mantine/notifications";
import { confirmDialog } from "@/components/modals/confirmDialog";
import { AppDrawer } from "@/components/drawers/AppDrawer";
import { OrbitalDots } from "@/components/loaders/OrbitalDots";
import { AuthorizationDetails } from "./AuthorizationDetails";
import { TestConnectionSection } from "./TestConnectionSection";
import { CredentialRotateSection } from "./CredentialRotateSection";
import { useNavigate } from "@/routing/accountRouting";
import { useBranding } from "@/context/BrandingContext";
import { useFacebookOAuthPopup } from "@/hooks/useFacebookOAuthPopup";
import { useShopifyOAuthPopup } from "@/hooks/useShopifyOAuthPopup";
import {
  enableShopifyPersonalization,
  exchangeShopifyOAuth,
  getFacebookReauthUrl,
  getShopifyCostAccess,
  personalizationGrantNeeded,
  reconnectShopifyOAuth,
  rotateBrevoKey,
  rotateActiveCampaignKey,
  rotateKlaviyoKey,
  setActiveCampaignEventTracking,
  setStorefrontPassword,
  submitFacebookReauth,
  type ShopifyCostAccess,
  type ShopifyPersonalizationStatus,
} from "@/api/authorizations";
import { apiFetch, ApiError } from "@/api/client";
import type {
  AuthorizationType,
  AuthorizationDTO,
} from "@/types/authorizations";

type DataSourceSummary = {
  id: string; // ds_...
  name: string;
  type?: string;
  status?: string;
  updated_at?: string | null;
};

type Props = {
  opened: boolean;
  onClose: () => void;

  backendUrl: string;
  isAccountAdmin: boolean;
  isSuperadmin?: boolean;

  authorization: AuthorizationDTO | null;
  onChanged?: () => void;

  /** Overrides the drawer's stacking order. Defaults to the enclosing modal
   *  layer (Mantine's 200 at the top level). The assistant reconnect handoff passes 300 so the drawer sits above
   *  the assistant drawer while staying below notifications (400). */
  zIndex?: number;
};

function toastError(title: string, message: string) {
  notifications.show({ title, message, color: "red" });
}
function toastOk(title: string, message: string) {
  notifications.show({ title, message, color: "green" });
}

function normalizeEnumValue(v: any): string {
  if (!v) return "";
  const s = String(v);
  const parts = s.split(".");
  return parts[parts.length - 1];
}

function extractErrorMessage(body: any): string {
  return String(
    body?.error?.message ?? body?.detail ?? body?.error ?? "Request failed"
  );
}

export function AuthorizationDrawer({
  opened,
  onClose,
  backendUrl,
  isAccountAdmin,
  isSuperadmin = false,
  authorization,
  onChanged,
  zIndex,
}: Props) {
  const authzId = authorization?.id ?? "";
  // The product cost copy names the product through the partner's branding.
  const { branding } = useBranding();

  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const [sources, setSources] = useState<DataSourceSummary[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState(false);

  const [newToken, setNewToken] = useState("");
  const [newApiKey, setNewApiKey] = useState("");
  const [rotating, setRotating] = useState(false);

  // ActiveCampaign event tracking (journey event steps): account ID + event key.
  const [eventActid, setEventActid] = useState("");
  const [eventKey, setEventKey] = useState("");
  const [savingEventTracking, setSavingEventTracking] = useState(false);

  // Re-auth (OAuth) flow state
  const stateRef = useRef<string | null>(null);
  const [reauthLaunching, setReauthLaunching] = useState(false);
  const [shopifyReconnectLaunching, setShopifyReconnectLaunching] = useState(false);
  const [missingScopes, setMissingScopes] = useState<string[] | null>(null);

  // Storefront personalization (Verity-managed KV) enable state.
  const [personalizationStatus, setPersonalizationStatus] =
    useState<ShopifyPersonalizationStatus | null>(null);
  const [personalizationEnabling, setPersonalizationEnabling] = useState(false);
  const [personalizationError, setPersonalizationError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Set just before launching a personalization reconnect so the shared Shopify
  // reconnect completion knows to refresh the personalization status afterward.
  const pendingPersonalizationRef = useRef(false);
  // Product cost access (`read_inventory`): null until the status has loaded,
  // and on any load failure (the notice is a nudge, never a blocker).
  const [costAccess, setCostAccess] = useState<ShopifyCostAccess | null>(null);
  const pendingCostAccessRef = useRef(false);

  const refreshCostAccess = useCallback(async () => {
    if (!authzId) return;
    try {
      setCostAccess(await getShopifyCostAccess(authzId));
    } catch {
      setCostAccess(null);
    }
  }, [authzId]);

  const typeLabel = useMemo(
    () => normalizeEnumValue(authorization?.type) || "",
    [authorization?.type]
  );

  const authMethod =
    ((authorization?.config as Record<string, unknown> | undefined)?.auth_method ?? "") as
      | "oauth"
      | "access_token"
      | "";
  const isOAuth = authMethod === "oauth";

  if (
    import.meta.env.DEV &&
    authorization &&
    !authMethod &&
    normalizeEnumValue(authorization?.type) === "FACEBOOK"
  ) {
    // Legacy/unbackfilled row — should not happen after migration runs.
    // eslint-disable-next-line no-console
    console.warn(
      "AuthorizationDrawer: Facebook authorization has no config.auth_method; falling back to access-token UI",
      { authorizationId: authorization.id },
    );
  }

  useEffect(() => {
    if (!opened || !authorization) return;
    setName(authorization.name ?? "");
    setSources([]);
    setSourcesError(null);
    setSaving(false);
    setDeleting(false);
    setNewToken("");
    setRotating(false);
    setEventActid("");
    setEventKey("");
    setSavingEventTracking(false);
    setMissingScopes(null);
    setPersonalizationStatus(null);
    setPersonalizationError(null);
    setPersonalizationEnabling(false);
    pendingPersonalizationRef.current = false;
    pendingCostAccessRef.current = false;
    setCostAccess(null);
    stateRef.current = null;

    // Only admins can grant it, and only admins may read it.
    if (normalizeEnumValue(authorization.type) === "SHOPIFY" && isAccountAdmin) {
      void refreshCostAccess();
    }

    // fetch linked sources for this authz (UI convenience)
    void loadLinkedSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, authorization?.id]);

  const handleReauthCode = useCallback(
    async ({ code, state }: { code: string; state: string }) => {
      if (!authzId) return;
      setMissingScopes(null);
      try {
        await submitFacebookReauth(authzId, code, state);
        toastOk("Authorization refreshed", "Facebook token has been rotated.");
        onChanged?.();
      } catch (e: unknown) {
        // Structured scope-narrowing error → render a dedicated list.
        const detail =
          e instanceof ApiError && e.detail && typeof e.detail === "object"
            ? (e.detail as { missing_scopes?: unknown })
            : null;
        const missing =
          detail && Array.isArray(detail.missing_scopes) && detail.missing_scopes.length
            ? (detail.missing_scopes as string[])
            : null;
        if (missing) {
          setMissingScopes(missing);
        } else {
          const msg = e instanceof Error ? e.message : "Something went wrong";
          toastError("Re-authentication failed", msg);
        }
      } finally {
        setReauthLaunching(false);
      }
    },
    [authzId, onChanged],
  );

  const {
    openPopup: openReauthPopup,
    loading: reauthPopupLoading,
    error: reauthPopupError,
  } = useFacebookOAuthPopup({
    getExpectedState: () => stateRef.current,
    onCode: handleReauthCode,
  });

  async function launchReauth() {
    if (!authzId) return;
    if (!isAccountAdmin) return;
    setMissingScopes(null);
    setReauthLaunching(true);
    try {
      const { url, state } = await getFacebookReauthUrl(authzId);
      stateRef.current = state;
      openReauthPopup(url);
    } catch (e: any) {
      stateRef.current = null;
      toastError(
        "Could not start re-authentication",
        e?.message ?? "Something went wrong",
      );
    } finally {
      setReauthLaunching(false);
    }
  }

  // --- Shopify reconnect (refresh token / scopes in place, no delete) ---
  const handleShopifyReconnect = useCallback(
    async ({ query }: { query: string }) => {
      const wasPersonalization = pendingPersonalizationRef.current;
      pendingPersonalizationRef.current = false;
      const wasCostAccess = pendingCostAccessRef.current;
      pendingCostAccessRef.current = false;
      try {
        await exchangeShopifyOAuth(query);
        // Every reconnect asks for the full default scope set, so any of them
        // can add product cost access: re-read it (best-effort).
        void refreshCostAccess();
        if (wasPersonalization) {
          toastOk(
            "Theme access granted",
            "Verity can now read your theme sections for storefront personalization.",
          );
          // Refresh the personalization status so the drawer reflects the new
          // grant without a second click (best-effort — the reconnect itself
          // already succeeded).
          try {
            const status = await enableShopifyPersonalization(authzId);
            setPersonalizationStatus(status);
          } catch {
            /* leave the prior status; the merchant can re-check manually */
          }
        } else if (wasCostAccess) {
          toastOk(
            "Product cost access granted",
            `${branding.productName} can now read your product costs to calculate margin.`,
          );
        } else {
          toastOk("Connection refreshed", "Your Shopify connection has been reconnected.");
        }
        onChanged?.();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Something went wrong";
        toastError(
          wasPersonalization
            ? "Could not grant theme access"
            : wasCostAccess
              ? "Could not grant product cost access"
              : "Reconnect failed",
          msg,
        );
      } finally {
        setShopifyReconnectLaunching(false);
      }
    },
    [authzId, onChanged, refreshCostAccess, branding.productName],
  );

  const {
    openPopup: openShopifyReconnectPopup,
    loading: shopifyReconnectPopupLoading,
    error: shopifyReconnectPopupError,
  } = useShopifyOAuthPopup({
    getExpectedState: () => stateRef.current,
    onComplete: handleShopifyReconnect,
  });

  // `forCostAccess` only changes the wording of the completion toast: a plain
  // reconnect already asks for every default scope, `read_inventory` included.
  async function launchShopifyReconnect(forCostAccess = false) {
    if (!authzId) return;
    if (!isAccountAdmin) return;
    setShopifyReconnectLaunching(true);
    try {
      const { authorize_url, state } = await reconnectShopifyOAuth(authzId);
      stateRef.current = state;
      pendingCostAccessRef.current = forCostAccess;
      openShopifyReconnectPopup(authorize_url);
    } catch (e: any) {
      stateRef.current = null;
      pendingCostAccessRef.current = false;
      toastError(
        "Could not start reconnect",
        e?.message ?? "Something went wrong",
      );
    } finally {
      // The popup is now open; the button's spinner is driven by the popup hook.
      setShopifyReconnectLaunching(false);
    }
  }

  // --- Storefront password (password-protected dev/pre-launch stores) ---
  // Write-only: the stored value is never echoed back; the list response only
  // says whether one is saved (config.storefront_password_set).
  const [storefrontPwInput, setStorefrontPwInput] = useState("");
  const [storefrontPwSaving, setStorefrontPwSaving] = useState(false);
  const [storefrontPwSetLocal, setStorefrontPwSetLocal] = useState<boolean | null>(null);
  const storefrontPwSet =
    storefrontPwSetLocal ??
    Boolean(
      (authorization?.config as Record<string, unknown> | undefined)
        ?.storefront_password_set,
    );

  async function handleSaveStorefrontPassword(clear: boolean) {
    if (!authzId || !isAccountAdmin) return;
    const password = clear ? null : storefrontPwInput.trim();
    if (!clear && !password) return;
    setStorefrontPwSaving(true);
    try {
      const res = await setStorefrontPassword(authzId, password);
      setStorefrontPwSetLocal(res.storefront_password_set);
      setStorefrontPwInput("");
      toastOk(
        clear ? "Store password removed" : "Store password saved",
        clear
          ? "Verity no longer uses a password for this storefront."
          : "Verity can now read your studio sections while the store is password-protected.",
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toastError("Couldn't save the store password", msg);
    } finally {
      setStorefrontPwSaving(false);
    }
  }

  // Theme access is there but the web pixel scopes are not (a store that
  // granted personalization before 2026-10-06). The same grant adds them.
  const grantNeeded = personalizationGrantNeeded(personalizationStatus);
  const checkoutAccessMissing = grantNeeded === "checkout";

  // --- Storefront personalization: enable (provision managed KV) + report status ---
  async function handleEnablePersonalization() {
    if (!authzId) return;
    if (!isAccountAdmin) return;
    const wasEnabled = personalizationStatus?.enabled === true;
    setPersonalizationError(null);
    setPersonalizationEnabling(true);
    try {
      const status = await enableShopifyPersonalization(authzId);
      setPersonalizationStatus(status);
      // Enabling is a two-grant moment by design: the connector consent never
      // includes `read_themes` (optional scope, least privilege), so a fresh
      // enable that needs it would otherwise strand the merchant in front of a
      // second button they have to discover. Chain the theme-access popup
      // right here — but only on the ENABLE action; an explicit
      // "Re-check status" click stays a passive check.
      if (!wasEnabled && status.reconnect_required) {
        await handleGrantThemeAccess();
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      setPersonalizationError(msg);
    } finally {
      setPersonalizationEnabling(false);
    }
  }

  // Grant read_themes by reusing the Shopify reconnect popup with the
  // personalization scope. The completion (handleShopifyReconnect) refreshes the
  // status because pendingPersonalizationRef is set here first.
  async function handleGrantThemeAccess() {
    if (!authzId) return;
    if (!isAccountAdmin) return;
    setShopifyReconnectLaunching(true);
    try {
      const { authorize_url, state } = await reconnectShopifyOAuth(authzId, {
        withPersonalization: true,
      });
      stateRef.current = state;
      pendingPersonalizationRef.current = true;
      openShopifyReconnectPopup(authorize_url);
    } catch (e: any) {
      stateRef.current = null;
      pendingPersonalizationRef.current = false;
      toastError(
        "Could not start theme access",
        e?.message ?? "Something went wrong",
      );
    } finally {
      setShopifyReconnectLaunching(false);
    }
  }

  async function loadLinkedSources() {
    if (!opened) return;
    if (!authzId) return;

    setSourcesLoading(true);
    setSourcesError(null);

    try {
      const res = await apiFetch(
        `/authorizations/${encodeURIComponent(authzId)}/sources`,
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(extractErrorMessage(body));

      const list = Array.isArray(body?.sources) ? body.sources : [];
      setSources(
        list.map((s: any) => ({
          id: String(s?.id ?? ""),
          name: String(s?.name ?? ""),
          type: normalizeEnumValue(s?.type),
          status: normalizeEnumValue(s?.status),
          updated_at: s?.updated_at ?? null,
        }))
      );
    } catch (e: any) {
      setSources([]);
      setSourcesError(e?.message ?? "Could not load linked sources.");
    } finally {
      setSourcesLoading(false);
    }
  }

  async function save() {
    if (!authorization || !authzId) return;
    if (!isAccountAdmin) return;

    const newName = name.trim();
    if (!newName) return toastError("Validation", "Name cannot be empty");

    const payload: any = {};
    if (newName !== (authorization.name ?? "").trim()) payload.name = newName;

    if (Object.keys(payload).length === 0) {
      toastOk("No changes", "Nothing to update.");
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch(
        `/authorizations/${encodeURIComponent(authzId)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(extractErrorMessage(body));

      toastOk("Saved", "Authorization updated.");
      onChanged?.();
    } catch (e: any) {
      toastError("Save failed", e?.message ?? "Something went wrong");
    } finally {
      setSaving(false);
    }
  }

  async function deleteAuthorization() {
    if (!authorization || !authzId) return;
    if (!isAccountAdmin) return;

    // This drawer OWNS its raised layer, so it names its own zIndex: the
    // ModalsProvider renders the confirm outside this tree, and at its default
    // 201 the confirm opened behind the drawer (300).
    confirmDialog({
      title: "Delete authorization",
      children: (
        <Text size="sm">
          Delete authorization &quot;{authorization.name}&quot;? This cannot be undone.
          Note: it must not be linked to any sources.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmColor: "red",
      onConfirm: async () => {
        setDeleting(true);
        try {
          const res = await apiFetch(
            `/authorizations/${encodeURIComponent(authzId)}`,
            { method: "DELETE" }
          );

          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(extractErrorMessage(body));
          }

          toastOk("Deleted", "Authorization removed.");
          onChanged?.();
          onClose();
        } catch (e: any) {
          toastError("Delete failed", e?.message ?? "Something went wrong");
        } finally {
          setDeleting(false);
        }
      },
    }, { zIndex });
  }

  const isFacebook = normalizeEnumValue(authorization?.type) === "FACEBOOK";
  const isShopify = normalizeEnumValue(authorization?.type) === "SHOPIFY";
  const isBrevo = normalizeEnumValue(authorization?.type) === "BREVO";
  const isKlaviyo = normalizeEnumValue(authorization?.type) === "KLAVIYO";
  const isActiveCampaign = normalizeEnumValue(authorization?.type) === "ACTIVECAMPAIGN";

  // Replace the API key on an API-key connector (Brevo/Klaviyo/ActiveCampaign). The backend
  // verifies the new key resolves to the SAME upstream account and auto-resumes
  // any sources that were paused because the old key went invalid.
  async function rotateApiKey() {
    if (!authorization || !authzId) return;
    if (!isAccountAdmin) return;
    const key = newApiKey.trim();
    if (!key) return toastError("Validation", "API key cannot be empty");

    setRotating(true);
    try {
      if (isBrevo) await rotateBrevoKey(authzId, key);
      else if (isKlaviyo) await rotateKlaviyoKey(authzId, key);
      else if (isActiveCampaign) await rotateActiveCampaignKey(authzId, key);
      else return;
      toastOk("API key updated", "The connection has been reconnected.");
      setNewApiKey("");
      onChanged?.();
    } catch (e: any) {
      toastError(
        "Update failed",
        e instanceof ApiError ? e.message : e?.message ?? "Something went wrong",
      );
    } finally {
      setRotating(false);
    }
  }

  // The event tracking account ID is public and lives in config; the event
  // key is stored encrypted and never returned.
  const eventTrackingActid = String(
    (authorization?.config as Record<string, unknown> | undefined)?.event_tracking_actid ?? "",
  ).trim();

  // Set the event tracking account ID and event key on an ActiveCampaign
  // connection. The backend checks their shape and that event tracking is
  // turned on in ActiveCampaign; its 400 detail is shown as is.
  async function saveEventTracking() {
    if (!authorization || !authzId) return;
    if (!isAccountAdmin || !isActiveCampaign) return;
    const actid = eventActid.trim();
    const key = eventKey.trim();
    if (!actid || !key) {
      return toastError("Validation", "Account ID and event key are required");
    }

    setSavingEventTracking(true);
    try {
      await setActiveCampaignEventTracking(authzId, actid, key);
      toastOk("Event tracking set up", "Journeys can now send events to ActiveCampaign.");
      setEventActid("");
      setEventKey("");
      onChanged?.();
    } catch (e: any) {
      toastError(
        "Event tracking setup failed",
        e instanceof ApiError ? e.message : e?.message ?? "Something went wrong",
      );
    } finally {
      setSavingEventTracking(false);
    }
  }

  async function rotateFacebookToken() {
    if (!authorization || !authzId) return;
    if (!isAccountAdmin) return;

    const token = newToken.trim();
    if (!token) return toastError("Validation", "Access token cannot be empty");

    setRotating(true);
    try {
      const res = await apiFetch(
        `/authorizations/${encodeURIComponent(authzId)}/rotate/facebook`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ access_token: token }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(extractErrorMessage(body));

      toastOk("Token updated", "Facebook access token has been rotated.");
      setNewToken("");
      onChanged?.();
    } catch (e: any) {
      toastError("Rotation failed", e?.message ?? "Something went wrong");
    } finally {
      setRotating(false);
    }
  }

  return (
    // AppDrawer publishes this drawer's raised layer itself, so a modal or
    // confirm opened from inside it matches it instead of landing behind it.
    <AppDrawer
      opened={opened}
      onClose={onClose}
      zIndex={zIndex}
      title={authorization?.name || "Authorization"}
      headerAside={
        typeLabel || authMethod ? (
          <Group gap="xs" wrap="nowrap">
            {typeLabel ? (
              <Badge variant="outline" color="gray">
                {typeLabel}
              </Badge>
            ) : null}
            {authMethod ? (
              <Badge variant="light" color={isOAuth ? "teal" : "gray"}>
                {isOAuth ? "OAUTH" : "ACCESS TOKEN"}
              </Badge>
            ) : null}
          </Group>
        ) : undefined
      }
    >
      {!authorization ? (
        <Text c="dimmed">No authorization selected.</Text>
      ) : (
        <Stack gap="md">
          {/* Settings */}
          <Stack gap="xs">
            <Text fw={600}>Settings</Text>

            <TextInput
              label="Name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              disabled={!isAccountAdmin}
              description={
                !isAccountAdmin
                  ? "Only account admins can edit settings."
                  : undefined
              }
            />

            <Group justify="flex-end">
              <Button
                onClick={save}
                loading={saving}
                disabled={!isAccountAdmin}
              >
                Save
              </Button>
            </Group>
          </Stack>

          <Divider />

          {/* Details */}
          <AuthorizationDetails
            type={authorization.type}
            config={authorization.config}
          />

          {isSuperadmin && (
            <>
              <Divider />
              <TestConnectionSection
                key={authzId}
                authorizationId={authzId}
                isSuperadmin={isSuperadmin}
                authType={typeLabel}
              />
            </>
          )}

          {isFacebook && isOAuth && (
            <>
              <Divider />

              {/* Re-authenticate (OAuth) */}
              <Stack gap="xs">
                <Text fw={600}>Re-authenticate</Text>
                <Text size="sm" c="dimmed">
                  Open Facebook in a popup to grant a fresh long-lived token. Your linked
                  data sources keep working without reconfiguration.
                </Text>

                {missingScopes && missingScopes.length > 0 && (
                  <Alert color="red" title="Missing scopes">
                    <Text size="sm" mb={6}>
                      The new token is missing permissions that were previously granted.
                      Please re-authorize with these scopes:
                    </Text>
                    <List size="sm" spacing={2}>
                      {missingScopes.map((s) => (
                        <List.Item key={s}>{s}</List.Item>
                      ))}
                    </List>
                  </Alert>
                )}

                {reauthPopupError && !missingScopes && (
                  <Alert color="red">{reauthPopupError}</Alert>
                )}

                <Group justify="flex-end">
                  <Button
                    variant="default"
                    color="teal"
                    leftSection={<IconRefresh size={16} />}
                    onClick={launchReauth}
                    loading={reauthLaunching || reauthPopupLoading}
                    disabled={!isAccountAdmin}
                  >
                    Re-authenticate
                  </Button>
                </Group>
              </Stack>
            </>
          )}

          {isShopify && (
            <>
              <Divider />

              {/* Reconnect (Shopify OAuth) — refresh token / permissions in place */}
              <Stack gap="xs">
                <Text fw={600}>Reconnect</Text>
                <Text size="sm" c="dimmed">
                  Open Shopify in a popup to refresh this connection&apos;s access and
                  permissions. Your linked data sources keep working without reconfiguration.
                </Text>

                {shopifyReconnectPopupError && (
                  <Alert color="red">{shopifyReconnectPopupError}</Alert>
                )}

                <Group justify="flex-end">
                  <Button
                    variant="default"
                    color="teal"
                    leftSection={<IconRefresh size={16} />}
                    onClick={() => void launchShopifyReconnect()}
                    loading={shopifyReconnectLaunching || shopifyReconnectPopupLoading}
                    disabled={!isAccountAdmin}
                  >
                    Reconnect
                  </Button>
                </Group>
              </Stack>

              {costAccess === "missing" && (
                <>
                  <Divider />

                  {/* Product cost (`read_inventory`) — margin needs it; older
                      connections predate the scope until they reconnect. */}
                  <Stack gap="xs">
                    <Text fw={600}>Product cost and margin</Text>
                    <Alert color="yellow">
                      {branding.productName} can&apos;t read your product costs yet. Grant inventory access so
                      margin can be calculated.
                    </Alert>
                    <Group justify="flex-end">
                      <Button
                        variant="default"
                        color="teal"
                        leftSection={<IconRefresh size={16} />}
                        onClick={() => void launchShopifyReconnect(true)}
                        loading={shopifyReconnectLaunching || shopifyReconnectPopupLoading}
                        disabled={!isAccountAdmin}
                      >
                        Grant product cost access
                      </Button>
                    </Group>
                  </Stack>
                </>
              )}

              <Divider />

              {/* Storefront personalization (Verity-managed KV) */}
              <Stack gap="xs">
                <Text fw={600}>Storefront personalization</Text>
                <Text size="sm" c="dimmed">
                  Turn on Verity-managed storefront personalization for this account.
                  Verity sets up a private audience store so you can show the right
                  content to the right visitor. Build campaigns afterwards.
                </Text>

                {personalizationStatus?.enabled && (
                  <Alert
                    color={
                      personalizationStatus.reconnect_required || checkoutAccessMissing
                        ? "yellow"
                        : "green"
                    }
                  >
                    {personalizationStatus.reconnect_required
                      ? "Personalization is on. To let Verity read your theme sections, grant theme access below."
                      : checkoutAccessMissing
                        ? "Storefront personalization is enabled. To link checkouts, including express checkouts, to your campaigns, grant checkout access below."
                        : "Storefront personalization is enabled."}
                  </Alert>
                )}

                {personalizationError && (
                  <Alert color="red">{personalizationError}</Alert>
                )}

                {/* Storefront password: only relevant while the store hides its
                    storefront behind /password (dev/pre-launch). Write-only. */}
                <PasswordInput
                  label="Store password"
                  description="Only needed while your store is password-protected (Online Store → Preferences in Shopify). Verity uses it to read your studio sections and prepare content."
                  placeholder={
                    storefrontPwSet
                      ? "A password is saved — type a new one to replace it"
                      : "Your storefront password"
                  }
                  value={storefrontPwInput}
                  onChange={(e) => setStorefrontPwInput(e.currentTarget.value)}
                  disabled={!isAccountAdmin}
                />
                <Group justify="flex-end" gap="xs">
                  {storefrontPwSet && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => void handleSaveStorefrontPassword(true)}
                      loading={storefrontPwSaving}
                      disabled={!isAccountAdmin}
                    >
                      Remove password
                    </Button>
                  )}
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => void handleSaveStorefrontPassword(false)}
                    loading={storefrontPwSaving}
                    disabled={!isAccountAdmin || !storefrontPwInput.trim()}
                  >
                    Save password
                  </Button>
                </Group>

                <Group justify="flex-end">
                  {personalizationStatus?.enabled &&
                    !personalizationStatus.reconnect_required && (
                      <Button
                        variant="light"
                        onClick={() => navigate("/activation/campaigns")}
                        disabled={!isAccountAdmin}
                      >
                        Go to campaigns →
                      </Button>
                    )}
                  {grantNeeded && (
                    <Button
                      variant="default"
                      color="teal"
                      leftSection={<IconRefresh size={16} />}
                      onClick={handleGrantThemeAccess}
                      loading={shopifyReconnectLaunching || shopifyReconnectPopupLoading}
                      disabled={!isAccountAdmin}
                    >
                      {grantNeeded === "theme" ? "Grant theme access" : "Grant checkout access"}
                    </Button>
                  )}
                  <Button
                    onClick={handleEnablePersonalization}
                    loading={personalizationEnabling}
                    disabled={!isAccountAdmin}
                  >
                    {personalizationStatus?.enabled
                      ? "Re-check status"
                      : "Enable personalization"}
                  </Button>
                </Group>
              </Stack>
            </>
          )}

          {isFacebook && !isOAuth && (
            <>
              <Divider />

              {/* Update Facebook access token (manual) */}
              <Stack gap="xs">
                <Text fw={600}>Update access token</Text>
                <Text size="sm" c="dimmed">
                  Replace the system user access token. The token will be validated before saving.
                </Text>

                <PasswordInput
                  label="New access token"
                  placeholder="Paste new system user token"
                  value={newToken}
                  onChange={(e) => setNewToken(e.currentTarget.value)}
                  disabled={!isAccountAdmin}
                />

                <Group justify="flex-end">
                  <Button
                    onClick={rotateFacebookToken}
                    loading={rotating}
                    disabled={!isAccountAdmin || !newToken.trim()}
                  >
                    Update token
                  </Button>
                </Group>
              </Stack>
            </>
          )}

          {(isBrevo || isKlaviyo || isActiveCampaign) && (
            <>
              <Divider />

              {/* Update API key (Brevo / Klaviyo / ActiveCampaign) — reconnects the connection
                  and auto-resumes any sources paused by the invalid key. */}
              <Stack gap="xs">
                <Text fw={600}>Update API key</Text>
                <Text size="sm" c="dimmed">
                  Paste a new API key to reconnect. It's validated against the same{" "}
                  {isBrevo ? "Brevo" : isActiveCampaign ? "ActiveCampaign" : "Klaviyo"} account before saving, and any sources
                  paused by the old key resume automatically.
                </Text>

                <PasswordInput
                  label="New API key"
                  placeholder="Paste new API key"
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.currentTarget.value)}
                  disabled={!isAccountAdmin}
                />

                <Group justify="flex-end">
                  <Button
                    onClick={rotateApiKey}
                    loading={rotating}
                    disabled={!isAccountAdmin || !newApiKey.trim()}
                  >
                    Update API key
                  </Button>
                </Group>
              </Stack>
            </>
          )}

          {isActiveCampaign && (
            <>
              <Divider />

              {/* Event tracking (ActiveCampaign): journey event steps send
                  events with the account ID and event key copied from AC. */}
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={600}>Event tracking</Text>
                  {eventTrackingActid ? (
                    <Badge color="green" variant="light">
                      Set up (account ID {eventTrackingActid})
                    </Badge>
                  ) : (
                    <Badge color="gray" variant="light">
                      Not set up
                    </Badge>
                  )}
                </Group>
                <Text size="sm" c="dimmed">
                  Journeys use event tracking to send events to ActiveCampaign.
                  Copy the account ID and event key from Settings &gt; Tracking &gt; Event
                  Tracking in ActiveCampaign, and make sure event tracking is turned on there.
                </Text>

                <TextInput
                  label="Account ID"
                  placeholder="Paste the event tracking account ID"
                  value={eventActid}
                  onChange={(e) => setEventActid(e.currentTarget.value)}
                  disabled={!isAccountAdmin}
                />
                <PasswordInput
                  label="Event key"
                  placeholder="Paste the event key"
                  value={eventKey}
                  onChange={(e) => setEventKey(e.currentTarget.value)}
                  disabled={!isAccountAdmin}
                />

                <Group justify="flex-end">
                  <Button
                    onClick={saveEventTracking}
                    loading={savingEventTracking}
                    disabled={!isAccountAdmin || !eventActid.trim() || !eventKey.trim()}
                  >
                    Save event tracking
                  </Button>
                </Group>
              </Stack>
            </>
          )}

          {/* Replace key for every connector with a generic credential
              form (data-driven; renders nothing for other types). */}
          {authorization && authzId && (
            <CredentialRotateSection
              authorizationId={authzId}
              authorizationType={normalizeEnumValue(authorization.type)}
              isAccountAdmin={isAccountAdmin}
              onChanged={onChanged}
            />
          )}

          <Divider />

          {/* Linked sources */}
          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text fw={600}>Linked sources</Text>
              <Button
                variant="subtle"
                size="xs"
                onClick={loadLinkedSources}
                loading={sourcesLoading}
              >
                Refresh
              </Button>
            </Group>

            <Text size="sm" c="dimmed">
              Sources that use this authorization.
            </Text>

            {sourcesError ? (
              <Text size="sm" c="red">
                {sourcesError}
              </Text>
            ) : null}

            <ScrollArea h={260} type="auto">
              <Stack gap="sm" pr="sm">
                {sourcesLoading && sources.length === 0 ? (
                  <Group>
                    <OrbitalDots size={20} />
                    <Text size="sm" c="dimmed">
                      Loading…
                    </Text>
                  </Group>
                ) : sources.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No sources linked.
                  </Text>
                ) : (
                  sources.map((s) => (
                    <Group key={s.id} justify="space-between" wrap="nowrap">
                      <div>
                        <Text fw={500}>{s.name}</Text>
                        <Text size="xs" c="dimmed">
                          {s.id}
                          {s.type ? ` · ${s.type}` : ""}
                          {s.status ? ` · ${s.status}` : ""}
                        </Text>
                      </div>
                    </Group>
                  ))
                )}
              </Stack>
            </ScrollArea>
          </Stack>

          <Divider />

          <DeleteSection
            onDelete={deleteAuthorization}
            loading={deleting}
            disabled={!isAccountAdmin}
            buttonLabel="Delete authorization"
            description="Permanently delete this authorization. This only works when no sources are linked."
          />
        </Stack>
      )}
    </AppDrawer>
  );
}
